import {nextTick, ref, type Ref} from 'vue'
import {buildEquipePagePath} from '~/composables/equipe/useEquipe'
import {fetchMyOrgPositions, type MeOrgPosition} from '~/composables/equipe/useMyOrgPositions'
import {normalizeOrgNni} from '~/utils/orgNni'

import {useMe} from '~/composables/auth/useMe'

export function useGeolocateOrganigramme(loggedIn: Ref<boolean>) {
  const route = useRoute()
  const router = useRouter()
  const { me } = useMe()

  const geolocateLoading = ref(false)
  const showGeolocateEmptyModal = ref(false)
  const showGeolocateErrorModal = ref(false)
  const geolocateErrorMessage = ref('')
  const showGeolocateChoiceModal = ref(false)
  const geolocateChoices = ref<MeOrgPosition[]>([])

  async function navigateToOrgPosition(position: MeOrgPosition): Promise<void> {
    const userNni = me.value?.nni
    if (!userNni) {
      return
    }
    const path = buildEquipePagePath(position)
    const highlightNni = normalizeOrgNni(userNni)
    showGeolocateChoiceModal.value = false
    geolocateChoices.value = []

    const isSameEquipePage = route.path === path
    const currentHighlight = normalizeOrgNni(String(route.query.highlightNni ?? ''))
    const sameHighlightAlreadyInUrl =
      isSameEquipePage && currentHighlight === highlightNni && currentHighlight !== ''

    if (sameHighlightAlreadyInUrl) {
      const withoutHighlight = { ...route.query }
      delete withoutHighlight.highlightNni
      await router.replace({ path, query: withoutHighlight })
      await nextTick()
      await navigateTo({ path, query: { ...withoutHighlight, highlightNni } }, { replace: true })
      return
    }

    const queryPayload = isSameEquipePage ? { ...route.query, highlightNni } : { highlightNni }
    await navigateTo({ path, query: queryPayload }, { replace: true })
  }

  async function onGeolocateOrganigramme(): Promise<void> {
    if (!loggedIn.value || geolocateLoading.value) {
      return
    }
    if (!me.value?.nni) {
      geolocateErrorMessage.value = 'Profil utilisateur indisponible. Réessayez dans un instant.'
      showGeolocateErrorModal.value = true
      return
    }

    geolocateLoading.value = true
    geolocateErrorMessage.value = ''
    try {
      const { positions } = await fetchMyOrgPositions()
      if (positions.length === 0) {
        showGeolocateEmptyModal.value = true
        return
      }
      if (positions.length === 1) {
        const onlyPosition = positions[0]
        if (onlyPosition) {
          await navigateToOrgPosition(onlyPosition)
        }
        return
      }
      geolocateChoices.value = positions
      showGeolocateChoiceModal.value = true
    } catch (error) {
      geolocateErrorMessage.value = error instanceof Error
        ? error.message
        : 'Impossible de vous situer dans l’organigramme.'
      showGeolocateErrorModal.value = true
    } finally {
      geolocateLoading.value = false
    }
  }

  function closeGeolocateChoiceModal(): void {
    showGeolocateChoiceModal.value = false
    geolocateChoices.value = []
  }

  function onGeolocateChoicePick(position: MeOrgPosition): void {
    void navigateToOrgPosition(position)
  }

  return {
    geolocateLoading,
    showGeolocateEmptyModal,
    showGeolocateErrorModal,
    geolocateErrorMessage,
    showGeolocateChoiceModal,
    geolocateChoices,
    onGeolocateOrganigramme,
    closeGeolocateChoiceModal,
    onGeolocateChoicePick,
  }
}
